import { questionText } from './question';
import type { AnalysisResult, CompletedAnswer, Operator } from './types';

export function analyzeAnswers(inputAnswers: CompletedAnswer[]): AnalysisResult {
  const answeredTimes = inputAnswers
    .map((answer) => answer.timeMs)
    .filter((timeMs): timeMs is number => typeof timeMs === 'number' && timeMs >= 0)
    .sort((left, right) => left - right);
  const medianAnsweredTimeMs = medianUpper(answeredTimes);
  const slowThresholdMs = medianAnsweredTimeMs * 1.5;

  const answers = inputAnswers.map((answer) => ({
    ...answer,
    isSlow: typeof answer.timeMs === 'number' && answer.timeMs > slowThresholdMs
  }));

  const categoryMap = new Map<string, { penalties: number[]; examples: Set<string> }>();

  for (const answer of answers) {
    if (answer.isCorrect && !answer.isSlow) {
      continue;
    }

    const penalty = answerPenalty(answer, medianAnsweredTimeMs);
    for (const label of labelsFor(answer.operator, answer.operand1, answer.operand2)) {
      const bucket = categoryMap.get(label) ?? { penalties: [], examples: new Set<string>() };
      bucket.penalties.push(penalty);
      bucket.examples.add(questionText(answer));
      categoryMap.set(label, bucket);
    }
  }

  const weakCategories = Array.from(categoryMap.entries())
    .map(([label, bucket]) => {
      const averagePenaltyMs = Math.round(
        bucket.penalties.reduce((sum, penalty) => sum + penalty, 0) / bucket.penalties.length
      );
      return {
        label,
        frequency: bucket.penalties.length,
        averagePenaltyMs,
        examples: Array.from(bucket.examples).slice(0, 3)
      };
    })
    .sort((left, right) => {
      const leftRank = left.frequency * left.averagePenaltyMs;
      const rightRank = right.frequency * right.averagePenaltyMs;
      return rightRank - leftRank || left.label.localeCompare(right.label);
    });

  return {
    medianAnsweredTimeMs,
    slowThresholdMs,
    slowest: answers
      .slice()
      .sort((left, right) => (right.timeMs ?? 0) - (left.timeMs ?? 0))
      .slice(0, 5),
    weakCategories,
    answers
  };
}

function medianUpper(values: number[]): number {
  if (values.length === 0) {
    return 0;
  }
  return values[Math.floor(values.length / 2)];
}

function answerPenalty(answer: CompletedAnswer, medianAnsweredTimeMs: number): number {
  const timeMs = answer.timeMs ?? medianAnsweredTimeMs;
  if (!answer.isCorrect) {
    return Math.max(timeMs - medianAnsweredTimeMs, medianAnsweredTimeMs);
  }
  return Math.max(0, timeMs - medianAnsweredTimeMs);
}

function labelsFor(operator: Operator, operand1: number, operand2: number): string[] {
  const prefix = operator === 'x' ? 'x' : '+';
  return Array.from(new Set([operand1, operand2])).map((operand) => `${prefix}${operand} facts`);
}
